import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import AuthPage from '@/pages/AuthPage';
import AuthCallback from '@/components/AuthCallback';
import AdminDashboard from '@/pages/AdminDashboard';
import CoachDashboard from '@/pages/CoachDashboard';
import PlayersPage from '@/pages/PlayersPage';
import SchedulePage from '@/pages/SchedulePage';
import PracticePage from '@/pages/PracticePage';
import VideosPage from '@/pages/VideosPage';
import PhotosPage from '@/pages/PhotosPage';
import StatsPage from '@/pages/StatsPage';
import CommunicationPage from '@/pages/CommunicationPage';
import CoachingCommunicationPage from '@/pages/CoachingCommunicationPage';
import ContactCardsPage from '@/pages/ContactCardsPage';
import SocialMediaPage from '@/pages/SocialMediaPage';
import WhiteboardPage from '@/pages/WhiteboardPage';
import ReportsPage from '@/pages/ReportsPage';
import SettingsPage from '@/pages/SettingsPage';
import AutomatedMessagesPage from '@/pages/AutomatedMessagesPage';
import PaymentResultPage from '@/pages/PaymentResultPage';
import AdminAnalyticsPage from '@/pages/AdminAnalyticsPage';
import PlayerDevelopmentPage from '@/pages/PlayerDevelopmentPage';
import SafetyEscalationsPage from '@/pages/SafetyEscalationsPage';
import CommandCenterPage from '@/pages/CommandCenterPage';
import CustomerDetailPage from '@/pages/CustomerDetailPage';
import RequestDemoPage from '@/pages/RequestDemoPage';
import SetPasswordPage from '@/pages/SetPasswordPage';
import OnboardingPage from '@/pages/OnboardingPage';
import axios from 'axios';
import '@/App.css';
import { requestNotificationPermission, saveFCMToken, onForegroundMessage } from '@/services/firebase';
import { toast } from 'sonner';
import { setSentryUserTags } from '@/instrument';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lifecycle Phase 1: platform header on every request (web now, Android already sends its own).
axios.defaults.headers.common['X-PAC-Platform'] = 'web';
// Hold-fix Sep 30 2026: web build version so the Platform Panel's version mix isn't 100% "unknown".
axios.defaults.headers.common['X-PAC-App-Version'] = process.env.REACT_APP_VERSION || 'web-dev';

// Inner component that has access to navigation
function AppContent() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [teams, setTeams] = useState([]);
  const [permissions, setPermissions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [maintenance, setMaintenance] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Lifecycle Phase 1: attach the JWT so new admin-gated /command-center routes work.
    // Existing routes never check this header, so this is a no-op for them.
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  useEffect(() => {
    // Phase 5 item 14 — opaque account_id tag only (never name/email) so
    // Sentry issues can be filtered per account without carrying any PII.
    setSentryUserTags({ accountId: user?.account_id || user?.id });
  }, [user]);

  useEffect(() => {
    // Stage E0/E switches served by /api/health: redirect_to (path-preserving, set at the flip) and maintenance (freeze banner).
    fetch(`${API}/health`).then(r => r.json()).then(h => {
      if (h.redirect_to && !window.location.origin.startsWith(h.redirect_to)) {
        window.location.replace(`${h.redirect_to}${window.location.pathname}${window.location.search}${window.location.hash}`);
        return;
      }
      setMaintenance(h.maintenance ? (h.maintenance_message || 'Saving is paused for about an hour.') : null);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser && token) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      loadUserTeams(userData.id);
      loadUserPermissions(userData.id, userData.team_id);
      // Re-register for push notifications on page refresh
      registerPushNotifications(userData.id, userData.team_id);
    }
    setIsLoading(false);
  }, [token]);

  const loadUserTeams = async (userId) => {
    try {
      const response = await axios.get(`${API}/user/teams?user_id=${userId}`);
      setTeams(response.data.teams || []);
    } catch (error) {
      console.error('Failed to load teams');
    }
  };

  const loadUserPermissions = async (userId, teamId) => {
    try {
      const response = await axios.get(`${API}/user/permissions?user_id=${userId}&team_id=${teamId}`);
      setPermissions(response.data);
    } catch (error) {
      console.error('Failed to load permissions');
      // Set default permissions
      setPermissions({
        is_head_coach: false,
        permissions: {
          players: true, schedule: true, stats: true, videos: true,
          communication: true, reports: true, practice_plans: true
        }
      });
    }
  };

  // Register device for push notifications
  const registerPushNotifications = async (userId, teamId) => {
    try {
      const token = await requestNotificationPermission();
      if (token) {
        await saveFCMToken(userId, teamId, token);
        console.log('Push notifications registered successfully');
      }
    } catch (error) {
      console.warn('Failed to register push notifications:', error);
    }
  };

  // Listen for foreground notifications when user is logged in
  useEffect(() => {
    if (user) {
      const unsubscribe = onForegroundMessage((payload) => {
        // Show toast notification for foreground messages
        toast.info(payload.notification?.title || 'New Notification', {
          description: payload.notification?.body
        });
      });
      return () => unsubscribe();
    }
  }, [user]);

  const [redirectAfterLogin, setRedirectAfterLogin] = useState(null);

  const handleLogin = (userData, authToken, redirectTo = '/') => {
    // Attach the JWT synchronously: the global API gate rejects any call made before the effect runs.
    axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', authToken);
    loadUserTeams(userData.id);
    loadUserPermissions(userData.id, userData.team_id);
    // Register for push notifications after login
    registerPushNotifications(userData.id, userData.team_id);
    // Defer navigation to the effect below — it must run AFTER `user` is
    // committed, otherwise the unauthenticated wildcard route can win the
    // race and bounce straight back to '/' (found by testing_agent).
    setRedirectAfterLogin(redirectTo);
  };

  useEffect(() => {
    if (user && redirectAfterLogin) {
      navigate(redirectAfterLogin);
      setRedirectAfterLogin(null);
    }
  }, [user, redirectAfterLogin]);

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    setTeams([]);
    setPermissions(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  // Expired/invalid session (admin tokens live 8h, coach tokens 7 days): the first 401 from our API signs the
  // user out with a clear message instead of leaving a dead session that fails silently on every call.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error.response?.status;
        const url = String(error.config?.url || '');
        const isAuthCall = /\/api\/auth\/(login|register|mfa|google|set-password)/.test(url);
        if (status === 401 && token && url.includes('/api/') && !isAuthCall) {
          handleLogout();
          toast.error('Your session has expired — please sign in again.', { id: 'session-expired' });
          navigate('/', { replace: true });
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSwitchTeam = async (teamId) => {
    try {
      const response = await axios.post(`${API}/user/switch-team?user_id=${user.id}&team_id=${teamId}`);
      const updatedUser = response.data.user;
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      loadUserPermissions(updatedUser.id, teamId);
      window.location.reload(); // Reload to refresh all data for new team
    } catch (error) {
      console.error('Failed to switch team');
    }
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  // Google OAuth callback: check URL fragment synchronously during render (must run before auth checks)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback onLogin={handleLogin} />;
  }

  // Public sales form (spec Section 6) — no auth required, must work before any login check.
  if (location.pathname.startsWith('/request-demo')) {
    return (
      <Routes>
        <Route path="/request-demo" element={<RequestDemoPage />} />
        <Route path="/request-demo/schedule/:leadId" element={<RequestDemoPage />} />
      </Routes>
    );
  }

  // Admin-invite set-password link — no auth required, must work before any login check.
  if (location.pathname.startsWith('/set-password')) {
    return <SetPasswordPage onLogin={handleLogin} />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="*" element={<AuthPage onLogin={handleLogin} />} />
      </Routes>
    );
  }

  return (
    <>
    {maintenance && (
      <div data-testid="maintenance-banner" role="status"
           style={{ position: 'sticky', top: 0, zIndex: 60, background: '#b45309', color: '#fff', padding: '10px 16px', textAlign: 'center', fontWeight: 600 }}>
        {maintenance}
      </div>
    )}
    <Routes>
      <Route path="/" element={
        user.role === 'head_coach' ? 
        <AdminDashboard user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} permissions={permissions} /> : 
        <CoachDashboard user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} permissions={permissions} />
      } />
      <Route path="/players" element={<PlayersPage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} permissions={permissions} />} />
      <Route path="/players/:playerId/development" element={<PlayerDevelopmentPage user={user} onLogout={handleLogout} />} />
      <Route path="/safety-escalations" element={<SafetyEscalationsPage user={user} onLogout={handleLogout} />} />
      <Route path="/command-center" element={<CommandCenterPage user={user} onLogout={handleLogout} />} />
      <Route path="/command-center/customers/:accountId" element={<CustomerDetailPage />} />
      <Route path="/onboarding" element={<OnboardingPage user={user} onLogout={handleLogout} />} />
      <Route path="/schedule" element={<SchedulePage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} permissions={permissions} />} />
      <Route path="/practice" element={<PracticePage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} permissions={permissions} />} />
      <Route path="/videos" element={<VideosPage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} permissions={permissions} />} />
      <Route path="/photos" element={<PhotosPage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} />} />
      <Route path="/stats" element={<StatsPage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} permissions={permissions} />} />
      <Route path="/whiteboard" element={<WhiteboardPage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} />} />
      <Route path="/communication" element={<CommunicationPage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} permissions={permissions} />} />
      <Route path="/coaching-communication" element={<CoachingCommunicationPage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} />} />
      <Route path="/contacts" element={<ContactCardsPage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} />} />
      <Route path="/social-media" element={<SocialMediaPage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} />} />
      <Route path="/reports" element={<ReportsPage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} permissions={permissions} />} />
      <Route path="/settings" element={<SettingsPage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} onUserUpdate={handleUserUpdate} permissions={permissions} />} />
      <Route path="/automated-messages" element={<AutomatedMessagesPage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} />} />
      <Route path="/payment/success" element={<PaymentResultPage />} />
      <Route path="/payment/cancel" element={<PaymentResultPage />} />
      <Route path="/admin/analytics" element={
        (user.role === 'head_coach' && (user.is_admin === true || user.role === 'admin' || (user.email || '').toLowerCase() === 'autumn.p.fisher1985@gmail.com'))
          ? <AdminAnalyticsPage user={user} onLogout={handleLogout} teams={teams} onSwitchTeam={handleSwitchTeam} />
          : <Navigate to="/" />
      } />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </>
  );
}

// Main App wrapper with BrowserRouter
function App() {
  return (
    <BrowserRouter>
      <div className="App">
        <AppContent />
        <Toaster />
      </div>
    </BrowserRouter>
  );
}

export default App;